<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { useVModel } from '@vueuse/core'
import { cn } from '@/lib/utils'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<{
  defaultValue?: string | number
  modelValue?: string | number
  class?: HTMLAttributes['class']
  disabled?: boolean
  suffixClass?: string
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | number): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
  passive: true,
  defaultValue: props.defaultValue,
})
</script>

<template>
  <div class="relative w-full">
    <!-- Prefix/Front Icon -->
    <div
      v-if="$slots.prefix"
      class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
    >
      <slot name="prefix" />
    </div>

    <!-- Input -->
    <input
      v-model="modelValue"
      data-slot="input"
      :disabled="disabled"
      v-bind="$attrs"
      :class="cn(
        'file:text-foreground text-neutral-700 placeholder:text-muted-foreground selection:bg-opacity-primary selection:text-neutral-700 dark:bg-input/30 border-input h-[40px] w-full leading-[40px]! min-w-0 rounded-md border bg-transparent text-sm transition-[color,box-shadow] outline-none file:inline-flex file:h-11 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        'focus-visible:border-primary focus-visible:ring-primary/50',
        'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
        $slots.prefix && 'pl-10',
        $slots.suffix && 'pr-10',
        props.class,
      )"
    >

    <!-- Suffix/Back Icon -->
    <div
      v-if="$slots.suffix"
      :class="cn('absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground', suffixClass)"
    >
      <slot name="suffix" />
    </div>
  </div>
</template>
